import React from 'react';
import type { AnswerOption } from '../types';

interface AnswerOptionsProps {
  options: AnswerOption[];
  onChoose: (optionId: string) => void;
  disabled?: boolean;
  chosenId?: string | null;
  showResult?: boolean;
  visible?: boolean;
}

const OPTION_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];

function optionStyle(
  option: AnswerOption,
  chosenId: string | null | undefined,
  showResult: boolean,
): string {
  const base =
    'flex items-center gap-1.5 w-full min-h-[36px] py-1.5 px-0 pr-[6px] bg-transparent text-left transition-all duration-200 disabled:cursor-not-allowed';

  if (!showResult || !chosenId) {
    return `${base} text-black hover:opacity-80 active:scale-[0.99]`;
  }

  const isChosen = option.id === chosenId;

  if (option.isCorrect) {
    return `${base} text-[#22C55E]`;
  }

  if (isChosen && !option.isCorrect) {
    return `${base} text-[#FF3B30]`;
  }

  return `${base} text-black`;
}

const AnswerOptions: React.FC<AnswerOptionsProps> = ({
  options,
  onChoose,
  disabled = false,
  chosenId,
  showResult = false,
  visible = true,
}) => {
  return (
    <div
      className={`flex flex-col w-full transition-opacity duration-300 ease-in-out ${
        visible ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
      style={{ gap: 'clamp(1px, 1vh, 8px)' }}
    >
      {options.map((option, idx) => {
        const letter = OPTION_LETTERS[idx] ?? String(idx + 1);
        const isChosen = option.id === chosenId;
        const currentTextColor =
          showResult && chosenId
            ? option.isCorrect
              ? 'text-[#22C55E]'
              : isChosen
                ? 'text-[#FF3B30]'
                : 'text-black'
            : 'text-black';

        return (
          <button
            key={option.id}
            onClick={() => onChoose(option.id)}
            disabled={disabled}
            className={`answer-option-row ${optionStyle(option, chosenId, showResult)}`}
            style={{ minHeight: 'clamp(18px, 2.8vh, 36px)' }}
          >
            <span className={`shrink-0 font-normal leading-none answer-option-text ${currentTextColor}`} style={{ fontSize: 'clamp(10px, min(1.8vw, 2.2vh), 16px)' }}>
              {letter}.
            </span>

            <span className={`flex-1 font-normal leading-[1.15] answer-option-text ${currentTextColor}`} style={{ fontSize: 'clamp(10px, min(1.8vw, 2.2vh), 16px)' }}>
              {option.text}
            </span>

            {showResult && isChosen && (
              <span className={`shrink-0 answer-option-text ${option.isCorrect ? 'text-[#22C55E]' : 'text-[#FF3B30]'}`} style={{ fontSize: 'clamp(10px, min(1.8vw, 2.2vh), 16px)' }}>
                {option.isCorrect ? '✓' : '✕'}
              </span>
            )}

            {showResult && !isChosen && option.isCorrect && (
              <span className="shrink-0 text-[#22C55E] answer-option-text" style={{ fontSize: 'clamp(10px, 2vw, 16px)' }}>✓</span>
            )}
          </button>
        );
      })}

      <style>{`
        @media (max-width: 639px) {
          .answer-option-text {
            font-size: clamp(14px, 4vh, 18px) !important;
          }
          .answer-option-row {
            min-height: clamp(32px, 6vh, 48px) !important;
          }
        }
      `}</style>
    </div>
  );
};

export default AnswerOptions;
